import React, { useState, useEffect } from "react";
import "./AdminPage.css";
import PricingAdmin from "./PricingAdmin";
import SettingsTab from "../components/admin/SettingsTab";
import BookingsTab from "../components/admin/BookingsTab";
import AdsManager from "../components/admin/AdsManager";

const AdminPage = () => {
  const formatNumberInput = (val) => {
    if (val === null || val === undefined) return "";
    return val
      .toString()
      .replace(/\D/g, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const parseNumberInput = (val) => {
    if (val === null || val === undefined) return "";
    return val.toString().replace(/\./g, "");
  };

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("admin123");
  const [loginError, setLoginError] = useState("");

  const [activeTab, setActiveTab] = useState("dashboard");
  const [vehicles, setVehicles] = useState([]);
  const [variants, setVariants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [selectedVehicleForVariants, setSelectedVehicleForVariants] =
    useState("");
  const [vehicleVariantsList, setVehicleVariantsList] = useState([]);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [variantForm, setVariantForm] = useState({
    variant_name: "",
    seating_capacity: "",
  });

  const [priceVariants, setPriceVariants] = useState([]);
  const [priceForm, setPriceForm] = useState({
    vehicle_id: "",
    variant_id: "",
    published_price: "",
    promotions: [
      { promotion_name: "", discount_type: "amount", discount_value: "" },
    ],
    other_fees: [{ other_fee_name: "", other_fee_amount: "" }],
  });

  const [specVariants, setSpecVariants] = useState([]);
  const [specForm, setSpecForm] = useState({
    engine: {},
    battery: {},
    safety: {},
    adas: {},
    exterior: {},
    interior: {},
    dimension: {},
    suspension: {},
    transmission: {},
    entertainment: {},
  });
  const [specVehicleId, setSpecVehicleId] = useState("");
  const [specVariantId, setSpecVariantId] = useState("");

  const [mediaVehicleId, setMediaVehicleId] = useState("");
  const [mediaList, setMediaList] = useState([]);
  const [vehicleColors, setVehicleColors] = useState([]);
  const [editingMediaId, setEditingMediaId] = useState(null);
  const [mediaForm, setMediaForm] = useState({
    media_type: "image",
    url: "",
    label: "",
    color_name: "",
    sort_order: 0,
  });

  const [colorsList, setColorsList] = useState([]);
  const [editingColorId, setEditingColorId] = useState(null);
  const [colorForm, setColorForm] = useState({
    color_code: "",
    color_name_vi: "",
    color_name_en: "",
    color_hex_code: "",
    color_hex_code_2: "",
    available: 1,
    is_vip: 0,
  });

  // ===== PROMOTIONS STATE =====
  const [promoList, setPromoList] = useState([]);
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [promoForm, setPromoForm] = useState({ title: "", link_url: "/khuyen-mai", description: "", is_active: 1 });
  const [promoImageFile, setPromoImageFile] = useState(null);
  const [promoImagePreview, setPromoImagePreview] = useState(null);

  // ===== VISIT STATS STATE =====
  const [visitStats, setVisitStats] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname !== "localhost" ? "/api" : "http://localhost:5000/api");

  // Fetch data only if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadVehicles();
      loadStats();
      loadColors(); // Load all shared colors on startup
    }
  }, [isAuthenticated]);

  // Reload tab-specific data when switching tabs
  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === "colors") loadColors();
    if (activeTab === "promotions") loadPromos();
    if (activeTab === "dashboard") loadVisitStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: passwordInput }),
      });
      const data = await response.json();
      if (data.success) {
        setIsAuthenticated(true);
      } else {
        setLoginError(data.message || "Sai mật khẩu");
      }
    } catch (err) {
      setLoginError("Lỗi kết nối đến máy chủ");
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/vehicles`);
      const data = await response.json();
      setVehicles(data);
    } catch (err) {
      setError("Lỗi tải danh sách xe");
      console.error(err);
    }
  };

  const loadVariants = async (vehicleId, setter) => {
    try {
      const response = await fetch(`${API_BASE_URL}/vehicles/${vehicleId}`);
      const data = await response.json();
      setter(data.variants || []);
    } catch (err) {
      setError("Lỗi tải phiên bản xe");
      console.error(err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats/dashboard`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMedia = async (vehicleId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/media/${vehicleId}`);
      const data = await response.json();
      setMediaList(data);
    } catch (err) {
      setError("Lỗi tải media xe");
      console.error(err);
    }
  };

  const handleVehicleInputChange = (e) => {
    const { name, value } = e.target;
    setVehicleForm({ ...vehicleForm, [name]: value });
  };

  const handlePriceInputChange = (e) => {
    const { name, value } = e.target;
    setPriceForm({ ...priceForm, [name]: value });
  };

  const handlePromotionChange = (index, field, value) => {
    const promotions = [...priceForm.promotions];
    promotions[index] = {
      ...promotions[index],
      [field]: value,
    };
    setPriceForm({ ...priceForm, promotions });
  };

  const addPromotionRow = () => {
    setPriceForm({
      ...priceForm,
      promotions: [
        ...priceForm.promotions,
        {
          promotion_name: "",
          discount_type: "amount",
          discount_value: "",
          is_default: true,
        },
      ],
    });
  };

  const removePromotionRow = (index) => {
    const promotions = priceForm.promotions.filter((_, idx) => idx !== index);
    setPriceForm({
      ...priceForm,
      promotions:
        promotions.length ? promotions : (
          [
            {
              promotion_name: "",
              discount_type: "amount",
              discount_value: "",
              is_default: true,
            },
          ]
        ),
    });
  };

  const handleFeeChange = (index, field, value) => {
    const other_fees = [...priceForm.other_fees];
    other_fees[index] = {
      ...other_fees[index],
      [field]: value,
    };
    setPriceForm({ ...priceForm, other_fees });
  };

  const addFeeRow = () => {
    setPriceForm({
      ...priceForm,
      other_fees: [
        ...priceForm.other_fees,
        {
          other_fee_name: "",
          other_fee_amount: "",
          is_default: true,
          selection_type: "checkbox",
          group_name: "",
        },
      ],
    });
  };

  const removeFeeRow = (index) => {
    const other_fees = priceForm.other_fees.filter((_, idx) => idx !== index);
    setPriceForm({
      ...priceForm,
      other_fees:
        other_fees.length ? other_fees : (
          [
            {
              other_fee_name: "",
              other_fee_amount: "",
              is_default: true,
              selection_type: "checkbox",
              group_name: "",
            },
          ]
        ),
    });
  };

  const handleSpecInputChange = (category, name, value) => {
    setSpecForm({
      ...specForm,
      [category]: {
        ...specForm[category],
        [name]: value,
      },
    });
  };

  const handleMediaInputChange = (e) => {
    const { name, value } = e.target;
    setMediaForm({ ...mediaForm, [name]: value });
  };

  const clearMessages = () => {
    setTimeout(() => {
      setSuccess("");
      setError("");
    }, 3000);
  };

  // ==================== VEHICLE HANDLERS ====================
  const handleAddVehicle = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/vehicles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vehicleForm),
      });

      if (response.ok) {
        setSuccess("Thêm xe thành công!");
        setVehicleForm({});
        loadVehicles();
        clearMessages();
      } else {
        setError("Lỗi thêm xe");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVehicle = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/vehicles/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vehicleForm),
      });

      if (response.ok) {
        setSuccess("Cập nhật xe thành công!");
        setEditingId(null);
        setVehicleForm({});
        loadVehicles();
        clearMessages();
      } else {
        setError("Lỗi cập nhật xe");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (window.confirm("Bạn chắc chắn muốn xóa xe này?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/vehicles/${id}`, {
          method: "DELETE",
        });

        if (response.ok) {
          setSuccess("Xóa xe thành công!");
          loadVehicles();
          clearMessages();
        } else {
          setError("Lỗi xóa xe");
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleEditVehicle = (vehicle) => {
    setEditingId(vehicle.vehicle_id);
    setVehicleForm({
      vehicle_code: vehicle.vehicle_code,
      vehicle_name: vehicle.vehicle_name,
      vehicle_type: vehicle.vehicle_type,
      segment: vehicle.segment,
      production_year: vehicle.production_year,
    });
  };

  const handleVariantVehicleChange = async (vehicleId) => {
    setSelectedVehicleForVariants(vehicleId);
    setVehicleVariantsList([]);
    setEditingVariantId(null);
    setVariantForm({ variant_name: "", seating_capacity: "" });
    if (vehicleId) {
      await loadVariants(vehicleId, setVehicleVariantsList);
    }
  };

  const handleAddVariant = async () => {
    if (!selectedVehicleForVariants) return;
    try {
      setLoading(true);
      const payload = {
        ...variantForm,
        vehicle_id: selectedVehicleForVariants,
      };
      const response = await fetch(`${API_BASE_URL}/admin/variants`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess("Thêm phiên bản thành công!");
        setVariantForm({ variant_name: "", seating_capacity: "" });
        loadVariants(selectedVehicleForVariants, setVehicleVariantsList);
        clearMessages();
      } else {
        setError("Lỗi thêm phiên bản");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVariant = async (id) => {
    try {
      setLoading(true);
      const payload = {
        ...variantForm,
        vehicle_id: selectedVehicleForVariants,
      };
      const response = await fetch(`${API_BASE_URL}/admin/variants/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess("Cập nhật phiên bản thành công!");
        setEditingVariantId(null);
        setVariantForm({ variant_name: "", seating_capacity: "" });
        loadVariants(selectedVehicleForVariants, setVehicleVariantsList);
        clearMessages();
      } else {
        setError("Lỗi cập nhật phiên bản");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVariant = async (id) => {
    if (window.confirm("Bạn chắc chắn muốn xóa phiên bản này?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/variants/${id}`, {
          method: "DELETE",
        });

        if (response.ok) {
          setSuccess("Xóa phiên bản thành công!");
          loadVariants(selectedVehicleForVariants, setVehicleVariantsList);
          clearMessages();
        } else {
          setError("Lỗi xóa phiên bản");
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleEditVariant = (variant) => {
    setEditingVariantId(variant.variant_id);
    setVariantForm({
      variant_name: variant.variant_name,
      seating_capacity: variant.seating_capacity,
    });
  };

  // ==================== PRICE HANDLERS ====================
  const parseArrayValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  const getDiscountAmount = () => {
    const published = Number(priceForm.published_price || 0);
    const promotions = parseArrayValue(priceForm.promotions);
    return promotions.reduce((total, promo) => {
      const type =
        promo.discount_type === "percentage" ? "percentage" : "amount";
      const amount = Number(promo.discount_value || 0);
      if (type === "percentage") {
        return total + Math.round((published * amount) / 100);
      }
      return total + amount;
    }, 0);
  };

  const getPromotionPrice = () => {
    return Math.max(
      Number(priceForm.published_price || 0) - getDiscountAmount(),
      0,
    );
  };

  const getTotalOtherFees = () => {
    return parseArrayValue(priceForm.other_fees).reduce(
      (total, fee) => total + Number(fee.other_fee_amount || 0),
      0,
    );
  };

  const getOnTheRoadPrice = () => {
    return Math.max(
      Number(priceForm.published_price || 0) +
        getTotalOtherFees() -
        getDiscountAmount(),
      0,
    );
  };

  const handleSelectPriceVariant = async (variantId) => {
    setPriceForm({ ...priceForm, variant_id: variantId });
    if (!variantId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/prices/${variantId}`);
      const data = await response.json();
      const promotions =
        data?.promotions ?
          parseArrayValue(data.promotions)
        : [
            {
              promotion_name: data?.promotion_name || "",
              discount_type: data?.discount_type || "amount",
              discount_value: data?.discount_value || "",
            },
          ];
      const other_fees =
        data?.other_fees ?
          parseArrayValue(data.other_fees)
        : [
            {
              other_fee_name: data?.other_fee_name || "",
              other_fee_amount: data?.other_fee_amount || "",
            },
          ];
      setPriceForm({
        ...priceForm,
        variant_id: variantId,
        published_price: data?.published_price || "",
        promotions,
        other_fees,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatePrice = async () => {
    try {
      setLoading(true);
      const payload = {
        ...priceForm,
        promotions: parseArrayValue(priceForm.promotions),
        other_fees: parseArrayValue(priceForm.other_fees),
      };
      const response = await fetch(`${API_BASE_URL}/admin/prices`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setSuccess("Cập nhật giá thành công!");
        loadVehicles();
        clearMessages();
      } else {
        setError("Lỗi cập nhật giá");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== SPECS HANDLERS ====================
  const handleSelectSpecVariant = async (variantId) => {
    setSpecVariantId(variantId);
    if (!variantId) {
      setSpecForm({
        engine: {},
        battery: {},
        safety: {},
        adas: {},
        exterior: {},
        interior: {},
        dimension: {},
        suspension: {},
        transmission: {},
        entertainment: {},
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/specs/${variantId}`);
      const data = await response.json();
      setSpecForm({
        engine: data.engine || {},
        battery: data.battery || {},
        safety: data.safety || {},
        adas: data.adas || {},
        exterior: data.exterior || {},
        interior: data.interior || {},
        dimension: data.dimension || {},
        suspension: data.suspension || {},
        transmission: data.transmission || {},
        entertainment: data.entertainment || {},
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateSpecs = async () => {
    if (!specVariantId) {
      setError("Vui lòng chọn phiên bản để cập nhật thông số.");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/admin/specs/all/${specVariantId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(specForm),
        },
      );
      if (response.ok) {
        setSuccess("Cập nhật thông số kỹ thuật thành công!");
        clearMessages();
      } else {
        setError("Lỗi cập nhật thông số");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== MEDIA HANDLERS ====================
  const handleMediaVehicleChange = async (vehicleId) => {
    setMediaVehicleId(vehicleId);
    setMediaList([]);
    setVehicleColors([]);
    handleCancelMediaEdit();
    if (!vehicleId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/vehicles/${vehicleId}`);
      const data = await response.json();
      setVehicleColors(data.colors || []);
      await loadMedia(vehicleId);
    } catch (err) {
      setError("Lỗi tải thông tin màu sắc xe");
      console.error(err);
    }
  };

  const handleSaveMedia = async () => {
    if (!mediaVehicleId || !mediaForm.url) {
      setError("Vui lòng chọn xe và nhập URL media.");
      return;
    }

    try {
      setLoading(true);
      const url =
        editingMediaId ?
          `${API_BASE_URL}/admin/media/${editingMediaId}`
        : `${API_BASE_URL}/admin/media`;
      const method = editingMediaId ? "PUT" : "POST";
      const payload =
        editingMediaId ?
          { ...mediaForm }
        : { vehicle_id: mediaVehicleId, ...mediaForm };

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess(
          editingMediaId ?
            "Cập nhật media thành công!"
          : "Đã thêm media thành công!",
        );
        setMediaForm({
          media_type: "image",
          url: "",
          label: "",
          color_name: "",
          sort_order: 0,
        });
        setEditingMediaId(null);
        loadMedia(mediaVehicleId);
        clearMessages();
      } else {
        setError(editingMediaId ? "Lỗi cập nhật media" : "Lỗi thêm media");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMedia = (item) => {
    setEditingMediaId(item.media_id);
    setMediaForm({
      media_type: item.media_type,
      url: item.url,
      label: item.label || "",
      color_name: item.color_name || "",
      sort_order: item.sort_order || 0,
    });
  };

  const handleCancelMediaEdit = () => {
    setEditingMediaId(null);
    setMediaForm({
      media_type: "image",
      url: "",
      label: "",
      color_name: "",
      sort_order: 0,
    });
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm("Bạn có chắc muốn xóa media này?")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/media/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setSuccess("Đã xóa media thành công!");
        loadMedia(mediaVehicleId);
        clearMessages();
      } else {
        setError("Lỗi xóa media");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ==================== VISIT STATS HANDLERS ====================
  const loadVisitStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats/visits`);
      const data = await response.json();
      setVisitStats(data);
    } catch (err) {
      console.error("Lỗi tải thống kê truy cập:", err);
    }
  };

  // ==================== PROMOTION HANDLERS ====================
  const loadPromos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/promotions`);
      const data = await response.json();
      setPromoList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Lỗi tải danh sách khuyến mãi");
      console.error(err);
    }
  };

  const handleSavePromo = async () => {
    if (!promoForm.title.trim()) {
      setError("Vui lòng nhập tiêu đề khuyến mãi");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", promoForm.title);
      formData.append("link_url", promoForm.link_url || "/khuyen-mai");
      formData.append("description", promoForm.description || "");
      formData.append("is_active", promoForm.is_active);
      if (promoImageFile) formData.append("image", promoImageFile);

      const url = editingPromoId
        ? `${API_BASE_URL}/admin/promotions/${editingPromoId}`
        : `${API_BASE_URL}/admin/promotions`;
      const method = editingPromoId ? "PUT" : "POST";

      const response = await fetch(url, { method, body: formData });
      if (!response.ok) throw new Error("Lỗi lưu khuyến mãi");

      setSuccess(editingPromoId ? "Cập nhật khuyến mãi thành công!" : "Thêm khuyến mãi thành công!");
      setPromoForm({ title: "", link_url: "/khuyen-mai", description: "", is_active: 1 });
      setPromoImageFile(null);
      setPromoImagePreview(null);
      setEditingPromoId(null);
      loadPromos();
    } catch (err) {
      setError("Lỗi: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPromo = (promo) => {
    setEditingPromoId(promo.promo_id);
    setPromoForm({
      title: promo.title || "",
      link_url: promo.link_url || "/khuyen-mai",
      description: promo.description || "",
      is_active: promo.is_active !== undefined ? promo.is_active : 1,
    });
    setPromoImagePreview(
      promo.image_url
        ? (promo.image_url.startsWith("http") ? promo.image_url : `${process.env.REACT_APP_BACKEND_URL || (window.location.hostname !== "localhost" ? "" : "http://localhost:5000")}${promo.image_url}`)
        : null
    );
    setPromoImageFile(null);
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm("Xóa khuyến mãi này?")) return;
    try {
      await fetch(`${API_BASE_URL}/admin/promotions/${id}`, { method: "DELETE" });
      setSuccess("Đã xóa khuyến mãi!");
      loadPromos();
    } catch (err) {
      setError("Lỗi xóa khuyến mãi");
    }
  };

  // ==================== COLOR HANDLERS ====================
  // Load tất cả màu dùng chung
  const loadColors = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/colors`);
      const data = await response.json();
      setColorsList(data || []);
    } catch (err) {
      setError("Lỗi tải danh sách màu sắc");
      console.error(err);
    }
  };

  const handleColorInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setColorForm({
      ...colorForm,
      [name]:
        type === "checkbox" ?
          checked ? 1
          : 0
        : value,
    });
  };

  const handleSaveColor = async () => {
    if (!colorForm.color_code || !colorForm.color_name_vi) {
      setError("Vui lòng nhập đầy đủ Mã Màu và Tên Màu (Tiếng Việt).");
      return;
    }

    try {
      setLoading(true);
      const url =
        editingColorId ?
          `${API_BASE_URL}/admin/colors/${editingColorId}`
        : `${API_BASE_URL}/admin/colors`;
      const method = editingColorId ? "PUT" : "POST";
      // vehicle_id = null => màu dùng chung
      const payload = { ...colorForm, vehicle_id: null };

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess(
          editingColorId ?
            "Cập nhật màu sắc thành công!"
          : "Đã thêm màu sắc thành công!",
        );
        setColorForm({
          color_code: "",
          color_name_vi: "",
          color_name_en: "",
          color_hex_code: "",
          color_hex_code_2: "",
          available: 1,
          is_vip: 0,
        });
        setEditingColorId(null);
        loadColors();
        clearMessages();
      } else {
        setError(editingColorId ? "Lỗi cập nhật màu sắc" : "Lỗi thêm màu sắc");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditColor = (item) => {
    setEditingColorId(item.color_id);
    setColorForm({
      color_code: item.color_code,
      color_name_vi: item.color_name_vi,
      color_name_en: item.color_name_en || "",
      color_hex_code: item.color_hex_code || "",
      color_hex_code_2: item.color_hex_code_2 || item.color_hex_code || "",
      available: item.available,
      is_vip: item.is_vip || 0,
    });
  };

  const handleCancelColorEdit = () => {
    setEditingColorId(null);
    setColorForm({
      color_code: "",
      color_name_vi: "",
      color_name_en: "",
      color_hex_code: "",
      color_hex_code_2: "",
      available: 1,
      is_vip: 0,
    });
  };

  const handleDeleteColor = async (id) => {
    if (!window.confirm("Bạn có chắc muốn xóa màu sắc này?")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/colors/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setSuccess("Đã xóa màu sắc thành công!");
        loadColors();
        clearMessages();
      } else {
        setError("Lỗi xóa màu sắc");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null || price === "") return "0 ₫";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      minimumFractionDigits: 0,
    }).format(Number(price));
  };

  if (!isAuthenticated) {
    return (
      <div
        className="admin-login-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <form
          onSubmit={handleLogin}
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            width: "400px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <h2 style={{ textAlign: "center", margin: 0, color: "#333" }}>
            Admin Login
          </h2>
          {loginError && (
            <div
              style={{
                color: "red",
                textAlign: "center",
                background: "#ffebee",
                padding: "10px",
                borderRadius: "4px",
              }}
            >
              {loginError}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label>Mật khẩu:</label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Nhập mật khẩu..."
              style={{
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{ padding: "12px" }}
          >
            {loading ? "Đang xử lý..." : "Đăng Nhập"}
          </button>
          <a
            href="/"
            style={{
              textAlign: "center",
              color: "#666",
              textDecoration: "none",
              fontSize: "14px",
            }}
          >
            ← Trở lại trang chủ
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <div className="admin-logo">
            <h2>Admin Panel</h2>
          </div>

          <nav className="admin-nav">
            <button
              className={`nav-item ${activeTab === "dashboard" ? "active" : ""}`}
              onClick={() => setActiveTab("dashboard")}
            >
              📊 Dashboard
            </button>
            <button
              className={`nav-item ${activeTab === "vehicles" ? "active" : ""}`}
              onClick={() => setActiveTab("vehicles")}
            >
              🚗 Quản Lý Xe
            </button>
            <button
              className={`nav-item ${activeTab === "prices" ? "active" : ""}`}
              onClick={() => setActiveTab("prices")}
            >
              💰 Quản Lý Giá
            </button>
            <button
              className={`nav-item ${activeTab === "specs" ? "active" : ""}`}
              onClick={() => setActiveTab("specs")}
            >
              🔧 Thông Số
            </button>
            <button
              className={`nav-item ${activeTab === "media" ? "active" : ""}`}
              onClick={() => setActiveTab("media")}
            >
              🖼️ Ảnh Xe
            </button>
            <button
              className={`nav-item ${activeTab === "colors" ? "active" : ""}`}
              onClick={() => setActiveTab("colors")}
            >
              🎨 Quản Lý Màu
            </button>
            <button
              className={`nav-item ${activeTab === "bookings" ? "active" : ""}`}
              onClick={() => setActiveTab("bookings")}
            >
              📋 Đơn Đặt Cọc
            </button>
            <button
              className={`nav-item ${activeTab === "promotions" ? "active" : ""}`}
              onClick={() => setActiveTab("promotions")}
            >
              🎁 Khuyến Mãi
            </button>
            <button
              className={`nav-item ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              ⚙️ Cài Đặt Giao Diện
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-content">
          {/* Messages */}
          {success && <div className="success-msg">{success}</div>}
          {error && <div className="error-msg">{error}</div>}

          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="tab-content">
              <h2>Dashboard</h2>
              {stats && (
                <div className="stats-grid">
                  <div className="stat-box">
                    <div className="stat-icon">🚗</div>
                    <div className="stat-info">
                      <h3>Tổng Xe</h3>
                      <p className="stat-number">{stats.totalVehicles}</p>
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">⚙️</div>
                    <div className="stat-info">
                      <h3>Tổng Phiên Bản</h3>
                      <p className="stat-number">{stats.totalVariants}</p>
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">📝</div>
                    <div className="stat-info">
                      <h3>Tổng Booking</h3>
                      <p className="stat-number">{stats.totalBookings}</p>
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">💵</div>
                    <div className="stat-info">
                      <h3>Tổng Cọc</h3>
                      <p className="stat-number">
                        {formatPrice(stats.totalRevenue)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Visit Stats */}
              <h3 style={{marginTop:32, marginBottom:16}}>📈 Lượt Truy Cập Thật</h3>
              {visitStats ? (
                <div className="stats-grid">
                  <div className="stat-box">
                    <div className="stat-icon">👁</div>
                    <div className="stat-info">
                      <h3>Hôm Nay</h3>
                      <p className="stat-number">{visitStats.today}</p>
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">📅</div>
                    <div className="stat-info">
                      <h3>7 Ngày Qua</h3>
                      <p className="stat-number">{visitStats.week}</p>
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">🗓</div>
                    <div className="stat-info">
                      <h3>30 Ngày Qua</h3>
                      <p className="stat-number">{visitStats.month}</p>
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">🌐</div>
                    <div className="stat-info">
                      <h3>Tổng Cộng</h3>
                      <p className="stat-number">{visitStats.total}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{color:'#888'}}>Đang tải dữ liệu truy cập...</p>
              )}
            </div>
          )}

          {/* Vehicles Tab */}
          {activeTab === "vehicles" && (
            <>
              <div className="tab-content">
                <h2>Quản Lý Xe</h2>

                {/* Add/Edit Form */}
                <div className="form-section">
                  <h3>{editingId ? "Sửa Xe" : "Thêm Xe Mới"}</h3>
                  <div className="form-grid">
                    <input
                      type="text"
                      name="vehicle_code"
                      placeholder="Mã Xe"
                      value={vehicleForm.vehicle_code || ""}
                      onChange={handleVehicleInputChange}
                    />
                    <input
                      type="text"
                      name="vehicle_name"
                      placeholder="Tên Xe"
                      value={vehicleForm.vehicle_name || ""}
                      onChange={handleVehicleInputChange}
                    />
                    <input
                      type="text"
                      name="vehicle_type"
                      placeholder="Loại Xe"
                      value={vehicleForm.vehicle_type || ""}
                      onChange={handleVehicleInputChange}
                    />
                    <input
                      type="text"
                      name="segment"
                      placeholder="Phân Khúc"
                      value={vehicleForm.segment || ""}
                      onChange={handleVehicleInputChange}
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        editingId ?
                          handleUpdateVehicle(editingId)
                        : handleAddVehicle()
                      }
                      disabled={loading}
                    >
                      {editingId ? "Cập Nhật" : "Thêm"}
                    </button>
                    {editingId && (
                      <button
                        className="btn btn-outline"
                        onClick={() => {
                          setEditingId(null);
                          setVehicleForm({});
                        }}
                      >
                        Hủy
                      </button>
                    )}
                  </div>
                </div>

                {/* Vehicle List */}
                <div className="list-section">
                  <h3>Danh Sách Xe</h3>
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Mã Xe</th>
                          <th>Tên Xe</th>
                          <th>Loại</th>
                          <th>Phân Khúc</th>
                          <th>Hành Động</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicles.map((vehicle) => (
                          <tr key={vehicle.vehicle_id}>
                            <td>{vehicle.vehicle_code}</td>
                            <td>{vehicle.vehicle_name}</td>
                            <td>{vehicle.vehicle_type}</td>
                            <td>{vehicle.segment}</td>
                            <td className="actions">
                              <button
                                className="btn-small btn-edit"
                                onClick={() => handleEditVehicle(vehicle)}
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-small btn-delete"
                                onClick={() =>
                                  handleDeleteVehicle(vehicle.vehicle_id)
                                }
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Variant Management Section */}
              <div className="tab-content" style={{ marginTop: "20px" }}>
                <h2>Quản Lý Phiên Bản Xe</h2>
                <div className="form-section">
                  <div className="form-group">
                    <label>Chọn Xe để quản lý phiên bản</label>
                    <select
                      value={selectedVehicleForVariants || ""}
                      onChange={(e) =>
                        handleVariantVehicleChange(e.target.value)
                      }
                    >
                      <option value="">-- Chọn xe --</option>
                      {vehicles.map((v) => (
                        <option key={v.vehicle_id} value={v.vehicle_id}>
                          {v.vehicle_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedVehicleForVariants && (
                    <>
                      <div className="form-grid" style={{ marginTop: "20px" }}>
                        <input
                          type="text"
                          placeholder="Mã Phiên Bản"
                          value={variantForm.variant_code || ""}
                          onChange={(e) =>
                            setVariantForm({
                              ...variantForm,
                              variant_code: e.target.value,
                            })
                          }
                        />
                        <input
                          type="text"
                          placeholder="Tên Phiên Bản"
                          value={variantForm.variant_name || ""}
                          onChange={(e) =>
                            setVariantForm({
                              ...variantForm,
                              variant_name: e.target.value,
                            })
                          }
                        />
                        <input
                          type="number"
                          placeholder="Số Chỗ Ngồi"
                          value={variantForm.seating_capacity || ""}
                          onChange={(e) =>
                            setVariantForm({
                              ...variantForm,
                              seating_capacity: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() =>
                            editingVariantId ?
                              handleUpdateVariant(editingVariantId)
                            : handleAddVariant()
                          }
                          disabled={loading}
                        >
                          {editingVariantId ?
                            "Cập Nhật Phiên Bản"
                          : "Thêm Phiên Bản"}
                        </button>
                        {editingVariantId && (
                          <button
                            className="btn btn-outline"
                            onClick={() => {
                              setEditingVariantId(null);
                              setVariantForm({
                                variant_name: "",
                                seating_capacity: "",
                              });
                            }}
                          >
                            Hủy
                          </button>
                        )}
                      </div>

                      <div
                        className="list-section"
                        style={{ marginTop: "20px" }}
                      >
                        <h4>Danh Sách Phiên Bản</h4>
                        <div className="table-responsive">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Mã Phiên Bản</th>
                                <th>Tên Phiên Bản</th>
                                <th>Số Chỗ</th>
                                <th>Hành Động</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vehicleVariantsList.map((variant) => (
                                <tr key={variant.variant_id}>
                                  <td>{variant.variant_id}</td>
                                  <td>{variant.variant_code}</td>
                                  <td>{variant.variant_name}</td>
                                  <td>{variant.seating_capacity}</td>
                                  <td className="actions">
                                    <button
                                      className="btn-small btn-edit"
                                      onClick={() => handleEditVariant(variant)}
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      className="btn-small btn-delete"
                                      onClick={() =>
                                        handleDeleteVariant(variant.variant_id)
                                      }
                                    >
                                      🗑️
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              {vehicleVariantsList.length === 0 && (
                                <tr>
                                  <td
                                    colSpan="4"
                                    style={{ textAlign: "center" }}
                                  >
                                    Chưa có phiên bản nào
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Prices Tab */}
          {activeTab === "prices" && (
             <PricingAdmin vehicles={vehicles} API_BASE_URL={API_BASE_URL} />
          )}

          {activeTab === "specs" && (
            <div className="tab-content">
              <h2>Thông Số Kỹ Thuật</h2>
              <div className="form-section">
                <div className="form-group">
                  <label>Chọn Xe</label>
                  <select
                    value={specVehicleId}
                    onChange={async (e) => {
                      const vehicleId = e.target.value;
                      setSpecVehicleId(vehicleId);
                      setSpecVariantId("");
                      setSpecForm({
                        engine: {},
                        battery: {},
                        safety: {},
                        adas: {},
                        exterior: {},
                        interior: {},
                        dimension: {},
                        suspension: {},
                        transmission: {},
                        entertainment: {},
                      });
                      setSpecVariants([]);
                      if (vehicleId) {
                        await loadVariants(vehicleId, setSpecVariants);
                      }
                    }}
                  >
                    <option value="">-- Chọn xe --</option>
                    {vehicles.map((v) => (
                      <option key={v.vehicle_id} value={v.vehicle_id}>
                        {v.vehicle_name}
                      </option>
                    ))}
                  </select>
                </div>

                {specVariants.length > 0 && (
                  <div className="form-group">
                    <label>Chọn Phiên Bản</label>
                    <select
                      value={specVariantId}
                      onChange={(e) => handleSelectSpecVariant(e.target.value)}
                    >
                      <option value="">-- Chọn phiên bản --</option>
                      {specVariants.map((v) => (
                        <option key={v.variant_id} value={v.variant_id}>
                          {v.variant_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <h3 className="spec-category-title">Động Cơ</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Loại động cơ</label>
                    <input
                      type="text"
                      placeholder="Loại động cơ"
                      className={
                        specForm.engine.engine_type ? "admin-input-filled" : ""
                      }
                      value={specForm.engine.engine_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "engine_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Dẫn động</label>
                    <input
                      type="text"
                      placeholder="Dẫn động"
                      className={
                        specForm.engine.drive_type ? "admin-input-filled" : ""
                      }
                      value={specForm.engine.drive_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "drive_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Số motor</label>
                    <input
                      type="number"
                      placeholder="Số motor"
                      className={
                        specForm.engine.motor_count ? "admin-input-filled" : ""
                      }
                      value={specForm.engine.motor_count || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "motor_count",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Công suất tối đa (kW)</label>
                    <input
                      type="number"
                      placeholder="Công suất tối đa (kW)"
                      className={
                        specForm.engine.max_power_kw ? "admin-input-filled" : ""
                      }
                      value={specForm.engine.max_power_kw || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "max_power_kw",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Mô-men xoắn tối đa (Nm)</label>
                    <input
                      type="number"
                      placeholder="Mô-men xoắn tối đa (Nm)"
                      className={
                        specForm.engine.max_torque_nm ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.engine.max_torque_nm || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "max_torque_nm",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Tốc độ tối đa (km/h)</label>
                    <input
                      type="number"
                      placeholder="Tốc độ tối đa (km/h)"
                      className={
                        specForm.engine.max_speed_kmh ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.engine.max_speed_kmh || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "max_speed_kmh",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Tăng tốc 0-100 km/h (s)</label>
                    <input
                      type="number"
                      placeholder="Tăng tốc 0-100 km/h (s)"
                      className={
                        specForm.engine.acceleration_0_100_seconds ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.engine.acceleration_0_100_seconds || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "engine",
                          "acceleration_0_100_seconds",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Pin & Sạc</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Loại pin</label>
                    <input
                      type="text"
                      placeholder="Loại pin"
                      className={
                        specForm.battery.battery_type ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.battery.battery_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "battery",
                          "battery_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Dung lượng pin (kWh)</label>
                    <input
                      type="number"
                      placeholder="Dung lượng pin (kWh)"
                      className={
                        specForm.battery.battery_capacity_kwh ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.battery.battery_capacity_kwh || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "battery",
                          "battery_capacity_kwh",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Quãng đường WLTP (km)</label>
                    <input
                      type="number"
                      placeholder="Quãng đường WLTP (km)"
                      className={
                        specForm.battery.wltp_range_km ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.battery.wltp_range_km || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "battery",
                          "wltp_range_km",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Quãng đường NEDC (km)</label>
                    <input
                      type="number"
                      placeholder="Quãng đường NEDC (km)"
                      className={
                        specForm.battery.nedc_range_km ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.battery.nedc_range_km || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "battery",
                          "nedc_range_km",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Sạc DC nhanh nhất (kW)</label>
                    <input
                      type="number"
                      placeholder="Sạc DC nhanh nhất (kW)"
                      className={
                        specForm.battery.dc_fast_charge_kw ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.battery.dc_fast_charge_kw || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "battery",
                          "dc_fast_charge_kw",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Thời gian sạc nhanh (phút)</label>
                    <input
                      type="number"
                      placeholder="Thời gian sạc nhanh (phút)"
                      className={
                        specForm.battery.fast_charge_time_minutes ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.battery.fast_charge_time_minutes || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "battery",
                          "fast_charge_time_minutes",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">An Toàn</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Túi khí trước</label>
                    <input
                      type="number"
                      placeholder="Túi khí trước"
                      className={
                        specForm.safety.front_airbags ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.front_airbags || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "front_airbags",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Túi khí rèm</label>
                    <input
                      type="number"
                      placeholder="Túi khí rèm"
                      className={
                        specForm.safety.curtain_airbags ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.curtain_airbags || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "curtain_airbags",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Túi khí hông trước</label>
                    <input
                      type="number"
                      placeholder="Túi khí hông trước"
                      className={
                        specForm.safety.side_airbags_front ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.side_airbags_front || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "side_airbags_front",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Túi khí trung tâm</label>
                    <input
                      type="number"
                      placeholder="Túi khí trung tâm"
                      className={
                        specForm.safety.center_airbags ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.center_airbags || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "center_airbags",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống ABS</label>
                    <input
                      type="text"
                      placeholder="Hệ thống ABS"
                      className={
                        specForm.safety.abs_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.abs_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "abs_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống EBD</label>
                    <input
                      type="text"
                      placeholder="Hệ thống EBD"
                      className={
                        specForm.safety.ebd_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.ebd_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "ebd_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống BA</label>
                    <input
                      type="text"
                      placeholder="Hệ thống BA"
                      className={
                        specForm.safety.ba_assist ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.ba_assist || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "ba_assist",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống ESC</label>
                    <input
                      type="text"
                      placeholder="Hệ thống ESC"
                      className={
                        specForm.safety.esc_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.esc_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "esc_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống TCS</label>
                    <input
                      type="text"
                      placeholder="Hệ thống TCS"
                      className={
                        specForm.safety.tcs_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.tcs_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "tcs_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ khởi hành ngang dốc (HSA)</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ khởi hành ngang dốc (HSA)"
                      className={
                        specForm.safety.hsa_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.hsa_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "hsa_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ đổ đèo (HDC)</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ đổ đèo (HDC)"
                      className={
                        specForm.safety.ess_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.ess_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "ess_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chống lật (ROM)</label>
                    <input
                      type="text"
                      placeholder="Chống lật (ROM)"
                      className={
                        specForm.safety.rom_system ? "admin-input-filled" : ""
                      }
                      value={specForm.safety.rom_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "rom_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cảnh báo điểm mù</label>
                    <input
                      type="text"
                      placeholder="Cảnh báo điểm mù"
                      className={
                        specForm.safety.blind_spot_warning ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.blind_spot_warning || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "blind_spot_warning",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cảnh báo phương tiện cắt ngang</label>
                    <input
                      type="text"
                      placeholder="Cảnh báo phương tiện cắt ngang"
                      className={
                        specForm.safety.cross_traffic_alert_rear ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.cross_traffic_alert_rear || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "cross_traffic_alert_rear",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cảnh báo áp suất lốp (TPMS)</label>
                    <input
                      type="text"
                      placeholder="Cảnh báo áp suất lốp (TPMS)"
                      className={
                        specForm.safety.tire_pressure_monitoring ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.safety.tire_pressure_monitoring || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "safety",
                          "tire_pressure_monitoring",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">ADAS</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Cruise Control Thích Ứng</label>
                    <input
                      type="text"
                      placeholder="Cruise Control Thích Ứng"
                      className={
                        specForm.adas.adaptive_cruise_control ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.adaptive_cruise_control || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "adaptive_cruise_control",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cảnh báo chệch làn</label>
                    <input
                      type="text"
                      placeholder="Cảnh báo chệch làn"
                      className={
                        specForm.adas.lane_departure_warning ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.lane_departure_warning || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "lane_departure_warning",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ giữ làn</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ giữ làn"
                      className={
                        specForm.adas.lane_keep_assist ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.lane_keep_assist || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "lane_keep_assist",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ định tâm làn</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ định tâm làn"
                      className={
                        specForm.adas.lane_centering ? "admin-input-filled" : ""
                      }
                      value={specForm.adas.lane_centering || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "lane_centering",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ lái khi tắc đường</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ lái khi tắc đường"
                      className={
                        specForm.adas.traffic_jam_assist ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.traffic_jam_assist || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "traffic_jam_assist",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ lái trên cao tốc</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ lái trên cao tốc"
                      className={
                        specForm.adas.highway_driving_assist ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.highway_driving_assist || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "highway_driving_assist",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cảnh báo va chạm trước</label>
                    <input
                      type="text"
                      placeholder="Cảnh báo va chạm trước"
                      className={
                        specForm.adas.forward_collision_warning ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.forward_collision_warning || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "forward_collision_warning",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Phanh khẩn cấp tự động</label>
                    <input
                      type="text"
                      placeholder="Phanh khẩn cấp tự động"
                      className={
                        specForm.adas.forward_automatic_braking ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.forward_automatic_braking || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "forward_automatic_braking",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Nhận diện biển báo</label>
                    <input
                      type="text"
                      placeholder="Nhận diện biển báo"
                      className={
                        specForm.adas.traffic_sign_recognition ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.traffic_sign_recognition || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "traffic_sign_recognition",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Giám sát người lái</label>
                    <input
                      type="text"
                      placeholder="Giám sát người lái"
                      className={
                        specForm.adas.driver_monitoring_system ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.driver_monitoring_system || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "driver_monitoring_system",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hỗ trợ đỗ xe tự động</label>
                    <input
                      type="text"
                      placeholder="Hỗ trợ đỗ xe tự động"
                      className={
                        specForm.adas.front_parking_assist ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.front_parking_assist || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "front_parking_assist",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Triệu hồi thông minh</label>
                    <input
                      type="text"
                      placeholder="Triệu hồi thông minh"
                      className={
                        specForm.adas.surround_view_monitoring ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.adas.surround_view_monitoring || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "adas",
                          "surround_view_monitoring",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Ngoại Thất</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Loại đèn pha</label>
                    <input
                      type="text"
                      placeholder="Loại đèn pha"
                      className={
                        specForm.exterior.headlight_type ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.headlight_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "headlight_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Đèn pha tự động</label>
                    <input
                      type="text"
                      placeholder="Đèn pha tự động"
                      className={
                        specForm.exterior.daytime_running_lights ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.daytime_running_lights || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "daytime_running_lights",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Đèn LED định vị ban ngày</label>
                    <input
                      type="text"
                      placeholder="Đèn LED định vị ban ngày"
                      className={
                        specForm.exterior.daytime_running_lights ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.daytime_running_lights || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "daytime_running_lights",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Loại đèn hậu</label>
                    <input
                      type="text"
                      placeholder="Loại đèn hậu"
                      className={
                        specForm.exterior.rear_lights ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.rear_lights || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "rear_lights",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Đèn sương mù</label>
                    <input
                      type="text"
                      placeholder="Đèn sương mù"
                      className={
                        specForm.exterior.fog_lights_front ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.fog_lights_front || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "fog_lights_front",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Gạt mưa</label>
                    <input
                      type="text"
                      placeholder="Gạt mưa"
                      className={
                        specForm.exterior.wiper_front ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.wiper_front || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "wiper_front",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Gương chiếu hậu</label>
                    <input
                      type="text"
                      placeholder="Gương chiếu hậu"
                      className={
                        specForm.exterior.side_mirror_material ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.side_mirror_material || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "side_mirror_material",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Tay nắm cửa</label>
                    <input
                      type="text"
                      placeholder="Tay nắm cửa"
                      className={
                        specForm.exterior.door_handle_type ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.door_handle_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "door_handle_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cửa sổ trời</label>
                    <input
                      type="text"
                      placeholder="Cửa sổ trời"
                      className={
                        specForm.exterior.panoramic_sunroof ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.panoramic_sunroof || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "panoramic_sunroof",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Thanh giá nóc</label>
                    <input
                      type="text"
                      placeholder="Thanh giá nóc"
                      className={
                        specForm.exterior.roof_trim ? "admin-input-filled" : ""
                      }
                      value={specForm.exterior.roof_trim || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "roof_trim",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Loại kính cửa sổ</label>
                    <input
                      type="text"
                      placeholder="Loại kính cửa sổ"
                      className={
                        specForm.exterior.window_type_description ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.exterior.window_type_description || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "exterior",
                          "window_type_description",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Nội Thất</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Chất liệu ghế</label>
                    <input
                      type="text"
                      placeholder="Chất liệu ghế"
                      className={
                        specForm.interior.seat_material ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.seat_material || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "seat_material",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chỉnh ghế lái</label>
                    <input
                      type="text"
                      placeholder="Chỉnh ghế lái"
                      className={
                        specForm.interior.driver_seat_adjustment ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.driver_seat_adjustment || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "driver_seat_adjustment",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chỉnh ghế phụ</label>
                    <input
                      type="text"
                      placeholder="Chỉnh ghế phụ"
                      className={
                        specForm.interior.passenger_seat_adjustment ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.passenger_seat_adjustment || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "passenger_seat_adjustment",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Sưởi ghế lái</label>
                    <input
                      type="text"
                      placeholder="Sưởi ghế lái"
                      className={
                        specForm.interior.driver_seat_heating ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.driver_seat_heating || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "driver_seat_heating",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Sưởi ghế phụ</label>
                    <input
                      type="text"
                      placeholder="Sưởi ghế phụ"
                      className={
                        specForm.interior.passenger_seat_heating ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.passenger_seat_heating || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "passenger_seat_heating",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Làm mát ghế lái</label>
                    <input
                      type="text"
                      placeholder="Làm mát ghế lái"
                      className={
                        specForm.interior.driver_seat_ventilation ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.driver_seat_ventilation || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "driver_seat_ventilation",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Làm mát ghế phụ</label>
                    <input
                      type="text"
                      placeholder="Làm mát ghế phụ"
                      className={
                        specForm.interior.passenger_seat_ventilation ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.passenger_seat_ventilation || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "passenger_seat_ventilation",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Kiểu vô lăng</label>
                    <input
                      type="text"
                      placeholder="Kiểu vô lăng"
                      className={
                        specForm.interior.steering_wheel_type ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.steering_wheel_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "steering_wheel_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chất liệu vô lăng</label>
                    <input
                      type="text"
                      placeholder="Chất liệu vô lăng"
                      className={
                        specForm.interior.steering_wheel_material ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.steering_wheel_material || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "steering_wheel_material",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Loại điều hòa</label>
                    <input
                      type="text"
                      placeholder="Loại điều hòa"
                      className={
                        specForm.interior.air_conditioning_type ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.air_conditioning_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "air_conditioning_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Kiểm soát chất lượng không khí</label>
                    <input
                      type="text"
                      placeholder="Kiểm soát chất lượng không khí"
                      className={
                        specForm.interior.air_quality_control ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.air_quality_control || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "air_quality_control",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Đèn viền nội thất</label>
                    <input
                      type="text"
                      placeholder="Đèn viền nội thất"
                      className={
                        specForm.interior.row2_heating ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.row2_heating || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "row2_heating",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Gương chiếu hậu trong</label>
                    <input
                      type="text"
                      placeholder="Gương chiếu hậu trong"
                      className={
                        specForm.interior.row2_ventilation ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.row2_ventilation || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "row2_ventilation",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Sạc không dây</label>
                    <input
                      type="text"
                      placeholder="Sạc không dây"
                      className={
                        specForm.interior.wireless_charging ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.interior.wireless_charging || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "interior",
                          "wireless_charging",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Kích Thước</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Chiều dài (mm)</label>
                    <input
                      type="number"
                      placeholder="Chiều dài (mm)"
                      className={
                        specForm.dimension.length_mm ? "admin-input-filled" : ""
                      }
                      value={specForm.dimension.length_mm || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "length_mm",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chiều rộng (mm)</label>
                    <input
                      type="number"
                      placeholder="Chiều rộng (mm)"
                      className={
                        specForm.dimension.width_mm ? "admin-input-filled" : ""
                      }
                      value={specForm.dimension.width_mm || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "width_mm",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chiều cao (mm)</label>
                    <input
                      type="number"
                      placeholder="Chiều cao (mm)"
                      className={
                        specForm.dimension.height_mm ? "admin-input-filled" : ""
                      }
                      value={specForm.dimension.height_mm || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "height_mm",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Trục cơ sở (mm)</label>
                    <input
                      type="number"
                      placeholder="Trục cơ sở (mm)"
                      className={
                        specForm.dimension.wheelbase_mm ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.dimension.wheelbase_mm || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "wheelbase_mm",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Khoảng sáng gầm (mm)</label>
                    <input
                      type="number"
                      placeholder="Khoảng sáng gầm (mm)"
                      className={
                        specForm.dimension.ground_clearance_mm ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.dimension.ground_clearance_mm || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "ground_clearance_mm",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Trọng lượng không tải (kg)</label>
                    <input
                      type="number"
                      placeholder="Trọng lượng không tải (kg)"
                      className={
                        specForm.dimension.curb_weight_kg ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.dimension.curb_weight_kg || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "curb_weight_kg",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Trọng tải (kg)</label>
                    <input
                      type="number"
                      placeholder="Trọng tải (kg)"
                      className={
                        specForm.dimension.front_cargo_capacity_l ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.dimension.front_cargo_capacity_l || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "front_cargo_capacity_l",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Thể tích khoang hành lý (L)</label>
                    <input
                      type="number"
                      placeholder="Thể tích khoang hành lý (L)"
                      className={
                        specForm.dimension.cargo_capacity_with_seats_l ?
                          "admin-input-filled"
                        : ""
                      }
                      value={
                        specForm.dimension.cargo_capacity_with_seats_l || ""
                      }
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "cargo_capacity_with_seats_l",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Thể tích gập ghế (L)</label>
                    <input
                      type="number"
                      placeholder="Thể tích gập ghế (L)"
                      className={
                        specForm.dimension.cargo_capacity_folded_seats_l ?
                          "admin-input-filled"
                        : ""
                      }
                      value={
                        specForm.dimension.cargo_capacity_folded_seats_l || ""
                      }
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "cargo_capacity_folded_seats_l",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Số chỗ ngồi</label>
                    <input
                      type="number"
                      placeholder="Số chỗ ngồi"
                      className={
                        specForm.dimension.gvwr_kg ? "admin-input-filled" : ""
                      }
                      value={specForm.dimension.gvwr_kg || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "dimension",
                          "gvwr_kg",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Hệ Thống Treo & Phanh</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Hệ thống treo trước</label>
                    <input
                      type="text"
                      placeholder="Hệ thống treo trước"
                      className={
                        specForm.suspension.front_suspension ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.front_suspension || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "front_suspension",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống treo sau</label>
                    <input
                      type="text"
                      placeholder="Hệ thống treo sau"
                      className={
                        specForm.suspension.rear_suspension ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.rear_suspension || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "rear_suspension",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Phanh trước</label>
                    <input
                      type="text"
                      placeholder="Phanh trước"
                      className={
                        specForm.suspension.front_brakes ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.front_brakes || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "front_brakes",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Phanh sau</label>
                    <input
                      type="text"
                      placeholder="Phanh sau"
                      className={
                        specForm.suspension.rear_brakes ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.rear_brakes || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "rear_brakes",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Kích thước mâm (inch)</label>
                    <input
                      type="number"
                      placeholder="Kích thước mâm (inch)"
                      className={
                        specForm.suspension.rim_size_inches ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.rim_size_inches || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "rim_size_inches",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Kích thước lốp</label>
                    <input
                      type="text"
                      placeholder="Kích thước lốp"
                      className={
                        specForm.suspension.tire_size ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.tire_size || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "tire_size",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Lốp dự phòng</label>
                    <input
                      type="text"
                      placeholder="Lốp dự phòng"
                      className={
                        specForm.suspension.brake_assist ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.suspension.brake_assist || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "suspension",
                          "brake_assist",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Truyền Động</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Loại hộp số</label>
                    <input
                      type="text"
                      placeholder="Loại hộp số"
                      className={
                        specForm.transmission.transmission_type ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.transmission.transmission_type || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "transmission",
                          "transmission_type",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Chế độ lái</label>
                    <input
                      type="text"
                      placeholder="Chế độ lái"
                      className={
                        specForm.transmission.drive_mode_selection ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.transmission.drive_mode_selection || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "transmission",
                          "drive_mode_selection",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Nút chuyển số</label>
                    <input
                      type="text"
                      placeholder="Nút chuyển số"
                      className={
                        specForm.transmission.gear_shifting_buttons ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.transmission.gear_shifting_buttons || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "transmission",
                          "gear_shifting_buttons",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Tính năng dừng tạm thời</label>
                    <input
                      type="text"
                      placeholder="Tính năng dừng tạm thời"
                      className={
                        specForm.transmission.idle_stop_feature ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.transmission.idle_stop_feature || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "transmission",
                          "idle_stop_feature",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <h3 className="spec-category-title">Giải Trí & Kết Nối</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Màn hình giải trí (inch)</label>
                    <input
                      type="number"
                      placeholder="Màn hình giải trí (inch)"
                      className={
                        specForm.entertainment.infotainment_screen_inches ?
                          "admin-input-filled"
                        : ""
                      }
                      value={
                        specForm.entertainment.infotainment_screen_inches || ""
                      }
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "infotainment_screen_inches",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Màn hình thông tin (inch)</label>
                    <input
                      type="number"
                      placeholder="Màn hình thông tin (inch)"
                      className={
                        specForm.entertainment.rear_screen_inches ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.rear_screen_inches || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "rear_screen_inches",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hiển thị kính lái (HUD)</label>
                    <input
                      type="text"
                      placeholder="Hiển thị kính lái (HUD)"
                      className={
                        specForm.entertainment.hud_display ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.hud_display || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "hud_display",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Apple CarPlay/Android Auto</label>
                    <input
                      type="text"
                      placeholder="Apple CarPlay/Android Auto"
                      className={
                        specForm.entertainment.apple_carplay_support ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.apple_carplay_support || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "apple_carplay_support",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Kết nối Bluetooth</label>
                    <input
                      type="text"
                      placeholder="Kết nối Bluetooth"
                      className={
                        specForm.entertainment.bluetooth_support ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.bluetooth_support || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "bluetooth_support",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Trạm phát WiFi</label>
                    <input
                      type="text"
                      placeholder="Trạm phát WiFi"
                      className={
                        specForm.entertainment.wifi_connectivity ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.wifi_connectivity || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "wifi_connectivity",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống dẫn đường</label>
                    <input
                      type="text"
                      placeholder="Hệ thống dẫn đường"
                      className={
                        specForm.entertainment.voice_assistant ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.voice_assistant || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "voice_assistant",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Điều khiển giọng nói</label>
                    <input
                      type="text"
                      placeholder="Điều khiển giọng nói"
                      className={
                        specForm.entertainment.twelve_volt_outlets ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.twelve_volt_outlets || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "twelve_volt_outlets",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cổng USB Type-A (trước)</label>
                    <input
                      type="number"
                      placeholder="Cổng USB Type-A (trước)"
                      className={
                        specForm.entertainment.front_usb_type_a_count ?
                          "admin-input-filled"
                        : ""
                      }
                      value={
                        specForm.entertainment.front_usb_type_a_count || ""
                      }
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "front_usb_type_a_count",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cổng USB Type-C (trước)</label>
                    <input
                      type="number"
                      placeholder="Cổng USB Type-C (trước)"
                      className={
                        specForm.entertainment.front_usb_type_c_count ?
                          "admin-input-filled"
                        : ""
                      }
                      value={
                        specForm.entertainment.front_usb_type_c_count || ""
                      }
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "front_usb_type_c_count",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cổng USB Type-A (sau)</label>
                    <input
                      type="number"
                      placeholder="Cổng USB Type-A (sau)"
                      className={
                        specForm.entertainment.row2_usb_type_a_count ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.row2_usb_type_a_count || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "row2_usb_type_a_count",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Cổng USB Type-C (sau)</label>
                    <input
                      type="number"
                      placeholder="Cổng USB Type-C (sau)"
                      className={
                        specForm.entertainment.row2_usb_type_c_count ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.row2_usb_type_c_count || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "row2_usb_type_c_count",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Số lượng loa</label>
                    <input
                      type="number"
                      placeholder="Số lượng loa"
                      className={
                        specForm.entertainment.speaker_count ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.speaker_count || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "speaker_count",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Hệ thống âm thanh cao cấp</label>
                    <input
                      type="text"
                      placeholder="Hệ thống âm thanh cao cấp"
                      className={
                        specForm.entertainment.speaker_brands ?
                          "admin-input-filled"
                        : ""
                      }
                      value={specForm.entertainment.speaker_brands || ""}
                      onChange={(e) =>
                        handleSpecInputChange(
                          "entertainment",
                          "speaker_brands",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleUpdateSpecs}
                  disabled={loading || !specVariantId}
                >
                  Lưu Thông Số
                </button>
              </div>
            </div>
          )}

          {activeTab === "media" && (
            <div className="tab-content">
              <h2>Ảnh Xe</h2>
              <div className="form-section">
                <h3>{editingMediaId ? "Sửa ảnh xe" : "Thêm ảnh xe mới"}</h3>
                <div className="form-group">
                  <label>Chọn Xe</label>
                  <select
                    value={mediaVehicleId}
                    onChange={(e) => handleMediaVehicleChange(e.target.value)}
                  >
                    <option value="">-- Chọn xe --</option>
                    {vehicles.map((v) => (
                      <option key={v.vehicle_id} value={v.vehicle_id}>
                        {v.vehicle_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-grid">
                  <div className="field-group">
                    <label>Màu Xe</label>
                    <select
                      name="color_name"
                      value={mediaForm.color_name || ""}
                      onChange={handleMediaInputChange}
                    >
                      <option value="">-- Chọn màu xe (tùy chọn) --</option>
                      {vehicleColors.map((color) => (
                        <option
                          key={color.color_id}
                          value={color.color_name_vi}
                        >
                          {color.color_name_en || color.color_name_vi}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field-group">
                    <label>Đường Dẫn Hình Ảnh (URL)</label>
                    <input
                      type="text"
                      name="url"
                      placeholder="Nhập URL hình ảnh (hoặc base64)"
                      value={mediaForm.url}
                      onChange={handleMediaInputChange}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveMedia}
                    disabled={loading || !mediaVehicleId}
                  >
                    {editingMediaId ? "Cập Nhật" : "Thêm"}
                  </button>
                  {editingMediaId && (
                    <button
                      className="btn btn-outline"
                      onClick={handleCancelMediaEdit}
                    >
                      Hủy
                    </button>
                  )}
                </div>
              </div>

              <div className="list-section">
                <h3>Danh sách ảnh xe</h3>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Màu Xe</th>
                        <th>Đường dẫn ảnh</th>
                        <th>Hành Động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mediaList.length === 0 ?
                        <tr>
                          <td colSpan="3" style={{ textAlign: "center" }}>
                            Chưa có hình ảnh nào được tải lên cho xe này
                          </td>
                        </tr>
                      : mediaList.map((item) => (
                          <tr key={item.media_id}>
                            <td>
                              {item.color_name ?
                                <span className="badge-color">
                                  {item.color_name}
                                </span>
                              : <span
                                  className="text-muted"
                                  style={{ fontStyle: "italic", color: "#888" }}
                                >
                                  Màu chung
                                </span>
                              }
                            </td>
                            <td
                              style={{
                                maxWidth: "400px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                title={item.url}
                              >
                                {item.url}
                              </a>
                            </td>
                            <td className="actions">
                              <button
                                className="btn-small btn-edit"
                                onClick={() => handleEditMedia(item)}
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-small btn-delete"
                                onClick={() => handleDeleteMedia(item.media_id)}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Colors Tab */}
          {activeTab === "colors" && (
            <div className="tab-content">
              <h2>Quản Lý Màu Sắc</h2>
              <p
                style={{
                  color: "#666",
                  marginBottom: "20px",
                  fontSize: "14px",
                }}
              >
                Danh sách màu sắc dùng chung cho tất cả các dòng xe VinFast
                trong hệ thống.
              </p>

              <div className="form-section">
                <h3>{editingColorId ? "Sửa màu sắc" : "Thêm màu sắc mới"}</h3>
                <div className="form-grid">
                  <div className="field-group">
                    <label>Mã Màu (Color Code) *</label>
                    <input
                      type="text"
                      name="color_code"
                      placeholder="VD: SOLAR_RUBY"
                      value={colorForm.color_code}
                      onChange={handleColorInputChange}
                    />
                  </div>

                  <div className="field-group">
                    <label>Tên Màu (Tiếng Việt) *</label>
                    <input
                      type="text"
                      name="color_name_vi"
                      placeholder="VD: Đỏ Mặt Trời"
                      value={colorForm.color_name_vi}
                      onChange={handleColorInputChange}
                    />
                  </div>

                  <div className="field-group">
                    <label>Tên Màu (Tiếng Anh)</label>
                    <input
                      type="text"
                      name="color_name_en"
                      placeholder="VD: Solar Ruby"
                      value={colorForm.color_name_en}
                      onChange={handleColorInputChange}
                    />
                  </div>

                  <div className="field-group">
                    <label>Màu Hex</label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="color"
                        value={colorForm.color_hex_code || "#cccccc"}
                        onChange={(e) =>
                          setColorForm({
                            ...colorForm,
                            color_hex_code: e.target.value,
                          })
                        }
                        style={{
                          width: "44px",
                          height: "38px",
                          padding: "2px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          border: "1px solid #ccc",
                          flexShrink: 0,
                        }}
                      />
                      <input
                        type="text"
                        name="color_hex_code"
                        placeholder="#dc143c"
                        value={colorForm.color_hex_code}
                        onChange={handleColorInputChange}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  {colorForm.is_vip === 1 && (
                    <div className="form-group">
                      <label>
                        Mã Hex Màu 2 (Màu VIP)
                        <span className="tooltip-icon" title="Mã màu thứ hai của màu VIP (vd: màu mái xe)">
                          ℹ️
                        </span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="color"
                          value={colorForm.color_hex_code_2 || colorForm.color_hex_code || "#cccccc"}
                          onChange={(e) =>
                            setColorForm({
                              ...colorForm,
                              color_hex_code_2: e.target.value,
                            })
                          }
                          style={{
                            width: "44px",
                            height: "38px",
                            padding: "2px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            border: "1px solid #ccc",
                            flexShrink: 0,
                          }}
                        />
                        <input
                          type="text"
                          name="color_hex_code_2"
                          placeholder="#ffffff"
                          value={colorForm.color_hex_code_2 || ""}
                          onChange={handleColorInputChange}
                          className="form-control"
                          style={{ flexGrow: 1 }}
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className="field-group"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "20px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="color_available"
                      name="available"
                      checked={colorForm.available === 1}
                      onChange={handleColorInputChange}
                    />
                    <label htmlFor="color_available">Khả dụng</label>
                  </div>

                  <div
                    className="field-group"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "12px",
                      padding: "10px 14px",
                      background: "linear-gradient(135deg, #fff8e1, #fff3cd)",
                      border: "1.5px solid #f59e0b",
                      borderRadius: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="color_is_vip"
                      checked={colorForm.is_vip === 1}
                      onChange={(e) => setColorForm({ ...colorForm, is_vip: e.target.checked ? 1 : 0 })}
                    />
                    <label htmlFor="color_is_vip" style={{ fontWeight: 600, color: '#92400e', cursor: 'pointer' }}>
                      ⭐ Màu VIP (hiển thị nổi bật hơn)
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveColor}
                    disabled={loading}
                  >
                    {editingColorId ? "Cập Nhật" : "Thêm Màu"}
                  </button>
                  {editingColorId && (
                    <button
                      className="btn btn-outline"
                      onClick={handleCancelColorEdit}
                    >
                      Hủy
                    </button>
                  )}
                </div>
              </div>

              <div className="list-section">
                <h3>Danh sách màu ({colorsList.length} màu)</h3>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Mẫu Màu</th>
                        <th>Mã Màu</th>
                        <th>Tên Tiếng Việt</th>
                        <th>Tên Tiếng Anh</th>
                        <th>Loại</th>
                        <th>Trạng Thái</th>
                        <th>Hành Động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {colorsList.length === 0 ?
                        <tr>
                          <td
                            colSpan="6"
                            style={{
                              textAlign: "center",
                              padding: "24px",
                              color: "#888",
                            }}
                          >
                            Chưa có màu sắc nào. Hãy thêm màu mới ở trên.
                          </td>
                        </tr>
                      : colorsList.map((item) => (
                          <tr key={item.color_id}>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <div
                                  className="color-preview-dot"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    background: item.is_vip === 1 
                                      ? `linear-gradient(135deg, ${item.color_hex_code || "#ccc"} 50%, ${item.color_hex_code_2 || item.color_hex_code || "#ccc"} 50%)`
                                      : (item.color_hex_code || "#ccc"),
                                    border: "2px solid #e0e0e0",
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                    flexShrink: 0,
                                  }}
                                  title={item.color_name_vi}
                                />
                                <span
                                  style={{ fontSize: "13px", color: "#666" }}
                                >
                                  {item.is_vip === 1 
                                    ? `${item.color_hex_code || "—"} / ${item.color_hex_code_2 || item.color_hex_code || "—"}`
                                    : (item.color_hex_code || "—")}
                                </span>
                              </div>
                            </td>
                            <td>
                              <code
                                style={{
                                  background: "#f5f5f5",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  fontSize: "13px",
                                }}
                              >
                                {item.color_code}
                              </code>
                            </td>
                            <td style={{ fontWeight: 500 }}>
                              {item.color_name_vi}
                            </td>
                            <td style={{ color: "#666" }}>
                              {item.color_name_en || "—"}
                            </td>
                            <td>
                              {item.is_vip === 1 ? (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '3px 10px', borderRadius: 50, fontSize: 12, fontWeight: 700,
                                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                  color: '#fff', boxShadow: '0 2px 6px rgba(245,158,11,0.4)'
                                }}>
                                  ⭐ VIP
                                </span>
                              ) : (
                                <span style={{
                                  padding: '3px 10px', borderRadius: 50, fontSize: 12,
                                  background: '#f3f4f6', color: '#6b7280', fontWeight: 500
                                }}>
                                  Thường
                                </span>
                              )}
                            </td>
                            <td>
                              {item.available === 1 ?
                                <span
                                  className="badge-color"
                                  style={{
                                    backgroundColor: "#e8f5e9",
                                    color: "#2e7d32",
                                    borderColor: "#c8e6c9",
                                  }}
                                >
                                  Khả dụng
                                </span>
                              : <span
                                  className="badge-color"
                                  style={{
                                    backgroundColor: "#ffebee",
                                    color: "#c62828",
                                    borderColor: "#ffcdd2",
                                  }}
                                >
                                  Ngưng dùng
                                </span>
                              }
                            </td>
                            <td className="actions">
                              <button
                                className="btn-small btn-edit"
                                onClick={() => handleEditColor(item)}
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-small btn-delete"
                                onClick={() => handleDeleteColor(item.color_id)}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <BookingsTab 
              onSuccess={(msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); }} 
              onError={(msg) => { setError(msg); setTimeout(() => setError(""), 3000); }} 
            />
          )}

          {/* Promotions Tab */}
          {activeTab === "promotions" && (
            <div className="tab-content">
              <h2>🎁 Quản Lý Khuyến Mãi</h2>

              {/* Add/Edit Form */}
              <div className="form-section">
                <h3>{editingPromoId ? "Sửa Khuyến Mãi" : "Thêm Khuyến Mãi Mới"}</h3>
                <div className="form-grid">
                  <input
                    type="text"
                    placeholder="Tiêu đề khuyến mãi"
                    value={promoForm.title}
                    onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Link khi bấm (mặc định /khuyen-mai)"
                    value={promoForm.link_url}
                    onChange={(e) => setPromoForm({ ...promoForm, link_url: e.target.value })}
                  />
                </div>
                <textarea
                  placeholder="Mô tả ngắn (không bắt buộc)"
                  value={promoForm.description}
                  onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                  style={{ width: '100%', minHeight: 80, marginTop: 12, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span>Ảnh quảng cáo:</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setPromoImageFile(file);
                          setPromoImagePreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </label>
                  {promoImagePreview && (
                    <img src={promoImagePreview} alt="preview" style={{ height: 80, borderRadius: 6, objectFit: 'cover' }} />
                  )}
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label>Hiển thị:</label>
                  <select
                    value={promoForm.is_active}
                    onChange={(e) => setPromoForm({ ...promoForm, is_active: Number(e.target.value) })}
                  >
                    <option value={1}>Bật</option>
                    <option value={0}>Tắt</option>
                  </select>
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                  <button className="btn-primary" onClick={handleSavePromo} disabled={loading}>
                    {editingPromoId ? "Cập Nhật" : "Thêm Mới"}
                  </button>
                  {editingPromoId && (
                    <button className="btn-secondary" onClick={() => {
                      setEditingPromoId(null);
                      setPromoForm({ title: "", link_url: "/khuyen-mai", description: "", is_active: 1 });
                      setPromoImageFile(null);
                      setPromoImagePreview(null);
                    }}>Hủy</button>
                  )}
                </div>
              </div>

              {/* Promo List */}
              <div className="table-section">
                <h3>Danh Sách Khuyến Mãi ({promoList.length})</h3>
                {promoList.length === 0 ? (
                  <p style={{color:'#888'}}>Chưa có khuyến mãi nào.</p>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ảnh</th>
                        <th>Tiêu Đề</th>
                        <th>Link</th>
                        <th>Trạng Thái</th>
                        <th>Thao Tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoList.map((promo) => (
                        <tr key={promo.promo_id}>
                          <td>
                            {promo.image_url ? (
                              <img
                                src={promo.image_url.startsWith('http') ? promo.image_url : `${process.env.REACT_APP_BACKEND_URL || (window.location.hostname !== "localhost" ? "" : "http://localhost:5000")}${promo.image_url}`}
                                alt={promo.title}
                                style={{ height: 56, width: 80, objectFit: 'cover', borderRadius: 6 }}
                              />
                            ) : <span style={{color:'#888'}}>Chưa có ảnh</span>}
                          </td>
                          <td><strong>{promo.title}</strong></td>
                          <td><small>{promo.link_url}</small></td>
                          <td>
                            <span style={{ padding: '3px 10px', borderRadius: 50, fontSize: 12, fontWeight: 600, background: promo.is_active ? '#dcfce7' : '#fee2e2', color: promo.is_active ? '#166534' : '#991b1b' }}>
                              {promo.is_active ? 'Đang bật' : 'Đã tắt'}
                            </span>
                          </td>
                          <td>
                            <button className="btn-edit" onClick={() => handleEditPromo(promo)} style={{marginRight:6}}>Sửa</button>
                            <button className="btn-delete" onClick={() => handleDeletePromo(promo.promo_id)}>Xóa</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <SettingsTab 
              onSuccess={(msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); }} 
              onError={(msg) => { setError(msg); setTimeout(() => setError(""), 3000); }} 
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
